/**
 * Nexus Admin Store
 * State management for admin dashboard
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { 
  nexusAuthApi, 
  nexusDashboardApi,
  Admin, 
  DashboardOverview 
} from '@/services/nexusApi'

interface NexusState {
  // Auth
  admin: Admin | null
  isAuthenticated: boolean
  isLoading: boolean
  
  // Dashboard
  overview: DashboardOverview | null
  overviewLoading: boolean
  
  // Actions
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  fetchAdmin: () => Promise<void>
  fetchOverview: () => Promise<void>
}

export const useNexusStore = create<NexusState>()(
  persist(
    (set) => ({
      // Initial state
      admin: null,
      isAuthenticated: false,
      isLoading: true,
      overview: null,
      overviewLoading: false,

      // Login
      login: async (email: string, password: string) => {
        try {
          const data = await nexusAuthApi.login(email, password)
          localStorage.setItem('nexus_access_token', data.access_token)
          set({ 
            admin: data.admin, 
            isAuthenticated: true,
            isLoading: false,
          })
          return true
        } catch (error) {
          console.error('Nexus login failed:', error)
          set({ isLoading: false })
          return false
        }
      },

      // Logout
      logout: async () => {
        try {
          await nexusAuthApi.logout()
        } catch {
          // Ignore errors
        }
        localStorage.removeItem('nexus_access_token')
        set({ admin: null, isAuthenticated: false })
      },

      // Fetch current admin
      fetchAdmin: async () => {
        const token = localStorage.getItem('nexus_access_token')
        if (!token) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }

        try {
          const admin = await nexusAuthApi.me()
          set({ admin, isAuthenticated: true, isLoading: false })
        } catch {
          localStorage.removeItem('nexus_access_token')
          set({ admin: null, isAuthenticated: false, isLoading: false })
        }
      },

      // Fetch dashboard overview
      fetchOverview: async () => {
        set({ overviewLoading: true })
        try {
          const overview = await nexusDashboardApi.getOverview()
          set({ overview, overviewLoading: false })
        } catch (error) {
          console.error('Failed to fetch overview:', error)
          set({ overviewLoading: false })
        }
      },
    }),
    {
      name: 'nexus-store',
      partialize: (state) => ({ 
        admin: state.admin,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

export default useNexusStore
